import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Boxes, CheckCircle2, FileText, PackageCheck, RefreshCw, RotateCcw, Truck } from "lucide-react";
import { Button, EmptyState, ErrorBanner, LoadingState, Modal, PageHeader, Panel, StatusBadge } from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import { goodsReceipts, warehouseName } from "../lib/demoData";
import { requireSupabase } from "../lib/supabase";
import { formatDateTime, loadWarehouseSnapshot, receiptStatusPresentation, type DeliveryNoteSnapshot, type WarehouseSnapshot } from "../lib/warehouseData";
import { useDeveloperSettings } from "../lib/developerSettings";

type WorkOrderSummary = {
  id: string;
  order_number: string;
  description: string | null;
};

type OrderReturnRow = {
  id: string;
  work_order_id: string;
  warehouse_id: string;
  status: "registered" | "picked_up";
  registration_note: string | null;
  registered_at: string;
  pickup_note: string | null;
  picked_up_at: string | null;
};

type PendingReturnAction =
  | { type: "register"; warehouseId: string; warehouseLabel: string }
  | { type: "complete"; warehouseId: string; warehouseLabel: string; orderReturn: OrderReturnRow };

const ORDER_NUMBER_PATTERN = /^[0-9]{7}-[0-9]{3}$/;

export function OrderPage() {
  const { exampleTexts } = useDeveloperSettings();
  const { orderNumber = "" } = useParams();
  const decodedOrderNumber = decodeURIComponent(orderNumber);
  const { cloudConfigured } = useAuth();
  const [workOrder, setWorkOrder] = useState<WorkOrderSummary | null>(null);
  const [snapshot, setSnapshot] = useState<WarehouseSnapshot | null>(null);
  const [returns, setReturns] = useState<OrderReturnRow[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(cloudConfigured);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [pendingAction, setPendingAction] = useState<PendingReturnAction | null>(null);

  const load = useCallback(async () => {
    setError("");
    if (!ORDER_NUMBER_PATTERN.test(decodedOrderNumber)) {
      setWorkOrder(null);
      setSnapshot(null);
      setLoading(false);
      return;
    }
    if (!cloudConfigured) {
      const demo = goodsReceipts.find((receipt) => receipt.orderNumber === decodedOrderNumber);
      setWorkOrder(demo ? { id: demo.id, order_number: demo.orderNumber, description: null } : null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const client = requireSupabase();
      const { data: orderData, error: orderError } = await client
        .from("work_orders")
        .select("id,order_number,description")
        .eq("order_number", decodedOrderNumber)
        .maybeSingle();
      if (orderError) throw orderError;
      if (!orderData) {
        setWorkOrder(null);
        setSnapshot(null);
        setReturns([]);
        return;
      }
      const [warehouseSnapshot, returnResult] = await Promise.all([
        loadWarehouseSnapshot(),
        client.from("order_returns")
          .select("id,work_order_id,warehouse_id,status,registration_note,registered_at,pickup_note,picked_up_at")
          .eq("work_order_id", orderData.id)
          .order("registered_at", { ascending: false })
      ]);
      if (returnResult.error) throw returnResult.error;
      setWorkOrder(orderData as WorkOrderSummary);
      setSnapshot(warehouseSnapshot);
      setReturns((returnResult.data ?? []) as OrderReturnRow[]);
    } catch (nextError) {
      setError(`Auftrag konnte nicht geladen werden: ${nextError instanceof Error ? nextError.message : "Unbekannter Fehler"}`);
    } finally {
      setLoading(false);
    }
  }, [cloudConfigured, decodedOrderNumber]);

  useEffect(() => { void load(); }, [load]);

  const receipts = useMemo(() => snapshot?.receipts.filter((receipt) => receipt.workOrderId === workOrder?.id) ?? [], [snapshot, workOrder?.id]);
  const receiptIds = useMemo(() => new Set(receipts.map((receipt) => receipt.id)), [receipts]);
  const packages = useMemo(() => snapshot?.packages.filter((item) => receiptIds.has(item.receiptId)) ?? [], [receiptIds, snapshot]);
  const pickups = useMemo(() => snapshot?.pickups.filter((pickup) => pickup.orderNumbers.includes(decodedOrderNumber)) ?? [], [decodedOrderNumber, snapshot]);
  const warehouses = useMemo(() => {
    const result = new Map<string, { id: string; name: string }>();
    receipts.forEach((receipt) => result.set(receipt.warehouseId, { id: receipt.warehouseId, name: receipt.warehouseName }));
    return [...result.values()];
  }, [receipts]);
  const documents = useMemo(() => {
    const result = new Map<string, DeliveryNoteSnapshot>();
    receipts.flatMap((receipt) => receipt.documents).forEach((document) => result.set(document.id, document));
    return [...result.values()];
  }, [receipts]);

  async function registerReturn(warehouseId: string, warehouseLabel: string) {
    if (!workOrder) return;
    setBusyKey(`register-${warehouseId}`);
    setError("");
    setMessage("");
    try {
      const { error: rpcError } = await requireSupabase().rpc("register_order_return", {
        target_work_order_id: workOrder.id,
        target_warehouse_id: warehouseId,
        target_note: notes[warehouseId]?.trim() || null
      });
      if (rpcError) throw rpcError;
      setNotes((current) => ({ ...current, [warehouseId]: "" }));
      setMessage(`Auftrag ${workOrder.order_number} wurde in ${warehouseLabel} zur Retoure angemeldet.`);
      setPendingAction(null);
      await load();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Retoure konnte nicht angemeldet werden.");
    } finally {
      setBusyKey(null);
    }
  }

  async function completeReturn(orderReturn: OrderReturnRow, warehouseLabel: string) {
    if (!workOrder) return;
    setBusyKey(`complete-${orderReturn.id}`);
    setError("");
    setMessage("");
    try {
      const { error: rpcError } = await requireSupabase().rpc("complete_order_return", {
        target_return_id: orderReturn.id,
        target_note: notes[orderReturn.warehouse_id]?.trim() || null
      });
      if (rpcError) throw rpcError;
      setNotes((current) => ({ ...current, [orderReturn.warehouse_id]: "" }));
      setMessage(`Die Retoure für ${workOrder.order_number} wurde als abgeholt protokolliert.`);
      setPendingAction(null);
      await load();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Retourenabholung konnte nicht gespeichert werden.");
    } finally {
      setBusyKey(null);
    }
  }

  async function openDocument(document: DeliveryNoteSnapshot) {
    const documentWindow = window.open("", "_blank");
    if (!documentWindow) {
      setError("Das Dokumentfenster wurde vom Browser blockiert. Pop-ups für diese Seite erlauben.");
      return;
    }
    documentWindow.opener = null;
    documentWindow.document.write("<p style='font-family:Arial;padding:24px'>Lieferschein wird sicher geladen …</p>");
    try {
      const { data, error: signedUrlError } = await requireSupabase().storage.from("delivery-notes").createSignedUrl(document.storagePath, 600);
      if (signedUrlError || !data?.signedUrl) throw signedUrlError ?? new Error("Kein Dokumentlink erhalten.");
      documentWindow.location.replace(data.signedUrl);
    } catch (nextError) {
      documentWindow.close();
      setError(nextError instanceof Error ? nextError.message : "Lieferschein konnte nicht geöffnet werden.");
    }
  }

  if (loading) return <><PageHeader title={decodedOrderNumber || "Auftrag"} description="Auftragsdaten werden geladen …" /><LoadingState /></>;

  if (!workOrder) {
    return <>
      <PageHeader title="Auftrag nicht gefunden" description="Die Auftragsnummer ist ungültig oder für dieses Konto nicht sichtbar." />
      <Link className="button button--secondary" to="/lagerbestand"><ArrowLeft size={16} /> Zum Lagerbestand</Link>
    </>;
  }

  if (!cloudConfigured) {
    const demo = goodsReceipts.find((receipt) => receipt.orderNumber === decodedOrderNumber);
    return <>
      <PageHeader title={workOrder.order_number} description="Lokale Vorschau des zentralen Auftrags." action={<Link className="button button--secondary" to="/lagerbestand"><ArrowLeft size={16} /> Zurück</Link>} />
      {demo && <Panel title="Auftragsübersicht"><div className="order-summary-grid"><span><small>Lieferant</small><strong>{demo.supplier}</strong></span><span><small>Abteilung</small><strong>{demo.department}</strong></span><span><small>Lager</small><strong>{warehouseName(demo.warehouse)}</strong></span><span><small>Lagerplatz</small><strong>{demo.location}</strong></span></div></Panel>}
      <div className="notice-banner order-demo-notice">Retouren werden erst mit verbundener Testcloud gespeichert.</div>
    </>;
  }

  const stockedPackages = packages.filter((item) => item.status === "stocked").length;
  const openReturns = returns.filter((item) => item.status === "registered");

  return (
    <>
      <PageHeader
        title={workOrder.order_number}
        description={workOrder.description || "Zentrale Übersicht aller Lagerbewegungen und Dokumente zu diesem Auftrag."}
        action={<span className="page-header__actions"><Link className="button button--secondary" to="/lagerbestand"><ArrowLeft size={16} /> Lagerbestand</Link><Button variant="secondary" onClick={() => void load()} disabled={loading}><RefreshCw size={16} /> Aktualisieren</Button></span>}
      />
      {error && <ErrorBanner message={error} />}
      {message && <div className="success-banner" role="status"><CheckCircle2 size={17} /><span>{message}</span></div>}

      <section className="order-kpi-grid">
        <article><span><PackageCheck size={20} /></span><small>Wareneingänge</small><strong>{receipts.length}</strong></article>
        <article><span><Boxes size={20} /></span><small>Packstücke im Lager</small><strong>{stockedPackages}</strong></article>
        <article><span><Truck size={20} /></span><small>Abholungen</small><strong>{pickups.length}</strong></article>
        <article className={openReturns.length ? "order-kpi--warning" : ""}><span><RotateCcw size={20} /></span><small>Retouren offen</small><strong>{openReturns.length}</strong></article>
      </section>

      <Panel title="Retoure" description="Der Status gilt je Lager und wird mit Benutzer und Zeitpunkt protokolliert.">
        {warehouses.length === 0 ? <EmptyState title="Kein Lagerbezug" description="Für diesen Auftrag ist kein bestätigter Wareneingang sichtbar." /> : (
          <div className="order-return-list">
            {warehouses.map((warehouse) => {
              const warehouseReturns = returns.filter((item) => item.warehouse_id === warehouse.id);
              const activeReturn = warehouseReturns.find((item) => item.status === "registered");
              const latestCompleted = warehouseReturns.find((item) => item.status === "picked_up");
              const warehouseStockedPackages = packages.filter((item) => item.warehouseId === warehouse.id && item.status === "stocked").length;
              return <article key={warehouse.id}>
                <header>
                  <div><strong>{warehouse.name}</strong><small>{activeReturn ? `Angemeldet ${formatDateTime(activeReturn.registered_at)}` : latestCompleted ? `Letzte Retoure abgeholt ${formatDateTime(latestCompleted.picked_up_at)}` : "Noch keine Retoure erfasst"}</small></div>
                  <StatusBadge tone={activeReturn ? "warning" : latestCompleted ? "success" : "neutral"}>{activeReturn ? "Retoure angemeldet" : latestCompleted ? "Retoure abgeholt" : "Keine Retoure"}</StatusBadge>
                </header>
                {activeReturn?.registration_note && <p className="return-note"><strong>Anmeldung:</strong> {activeReturn.registration_note}</p>}
                <label className="field"><span>{activeReturn ? "Notiz zur Abholung" : "Notiz zur Retoure (optional)"}</span><textarea className="input return-textarea" maxLength={2000} value={notes[warehouse.id] ?? ""} onChange={(event) => setNotes((current) => ({ ...current, [warehouse.id]: event.target.value }))} placeholder={activeReturn ? exampleTexts.optional : exampleTexts.returnReason} /></label>
                <div className="order-return-actions">
                  {activeReturn
                    ? <Button onClick={() => setPendingAction({ type: "complete", warehouseId: warehouse.id, warehouseLabel: warehouse.name, orderReturn: activeReturn })} disabled={busyKey === `complete-${activeReturn.id}`}><CheckCircle2 size={16} /> Retoure abgeholt</Button>
                    : warehouseStockedPackages > 0
                      ? <Button onClick={() => setPendingAction({ type: "register", warehouseId: warehouse.id, warehouseLabel: warehouse.name })} disabled={busyKey === `register-${warehouse.id}`}><RotateCcw size={16} /> Zur Retoure anmelden</Button>
                      : <span className="muted-text">Kein Material mehr im Lager</span>}
                </div>
                {warehouseReturns.length > 0 && <details className="return-history"><summary>Retourenverlauf ({warehouseReturns.length})</summary><div>{warehouseReturns.map((item) => <span key={item.id}><StatusBadge tone={item.status === "registered" ? "warning" : "success"}>{item.status === "registered" ? "Angemeldet" : "Abgeholt"}</StatusBadge><small>{formatDateTime(item.registered_at)}{item.picked_up_at ? ` · abgeholt ${formatDateTime(item.picked_up_at)}` : ""}</small></span>)}</div></details>}
              </article>;
            })}
          </div>
        )}
      </Panel>

      <div className="order-content-grid">
        <Panel title="Wareneingänge und Bestand" description="Alle Lieferungen zu dieser Auftragsnummer.">
          {receipts.length === 0 ? <EmptyState title="Keine Wareneingänge" description="Für das aktuelle Konto sind keine Lieferungen sichtbar." /> : <div className="order-receipt-list">{receipts.map((receipt) => {
            const presentation = receiptStatusPresentation(receipt);
            return <article key={receipt.id}><span className="record-row__icon"><PackageCheck size={18} /></span><span><strong>{receipt.supplier}</strong><small>{receipt.department} · {formatDateTime(receipt.receivedAt)}</small></span><span><strong>{receipt.warehouseName} · {receipt.location}</strong><small>{receipt.stockedPackages} von {receipt.packageCount} im Lager</small></span><StatusBadge tone={presentation.tone}>{presentation.label}</StatusBadge></article>;
          })}</div>}
        </Panel>

        <Panel title="Lieferscheine" description="Private Originaldokumente des Auftrags.">
          {documents.length === 0 ? <EmptyState title="Kein Lieferschein" description="Es wurde noch kein Dokument verknüpft." /> : <div className="order-document-list">{documents.map((document, index) => <article key={document.id}><span><FileText size={18} /></span><span><strong>{document.originalFileName || `Lieferschein ${index + 1}`}</strong><small>{document.documentSource === "network_scanner" ? "Netzwerkscanner" : "Lagergerät"} · {formatDateTime(document.capturedAt)}</small></span><Button variant="secondary" onClick={() => void openDocument(document)}><FileText size={15} /> Öffnen</Button></article>)}</div>}
        </Panel>
      </div>

      <Panel title="Abholungen" description="Ausgaben an Monteure zu diesem Auftrag.">
        {pickups.length === 0 ? <EmptyState title="Noch keine Abholung" description="Für diesen Auftrag wurde noch kein Packstück ausgegeben." /> : <div className="order-pickup-list">{pickups.map((pickup) => <article key={pickup.id}><span><Truck size={18} /></span><span><strong>{pickup.technician}</strong><small>{pickup.warehouseName} · {formatDateTime(pickup.signedAt ?? pickup.createdAt)}</small></span><span><strong>{pickup.packageCount} Packstück{pickup.packageCount === 1 ? "" : "e"}</strong><small>{pickup.packageCodes.join(", ")}</small></span><StatusBadge tone={pickup.signedAt ? "success" : "warning"}>{pickup.signedAt ? "Unterschrieben" : "Entwurf"}</StatusBadge></article>)}</div>}
      </Panel>

      {pendingAction && (
        <Modal
          title={pendingAction.type === "register" ? "Retoure anmelden" : "Retourenabholung bestätigen"}
          description={`${workOrder.order_number} · ${pendingAction.warehouseLabel}`}
          onClose={() => { if (!busyKey) setPendingAction(null); }}
        >
          <div className="return-confirmation">
            <span className={pendingAction.type === "register" ? "return-confirmation__icon" : "return-confirmation__icon return-confirmation__icon--complete"}>
              {pendingAction.type === "register" ? <RotateCcw size={26} /> : <CheckCircle2 size={26} />}
            </span>
            <div>
              <strong>{pendingAction.type === "register" ? "Diesen Auftrag jetzt zur Retoure anmelden?" : "Wurde die Retoure vollständig abgeholt?"}</strong>
              <p>{pendingAction.type === "register" ? "Der offene Retourenstatus ist anschließend für alle berechtigten Benutzer sichtbar." : "Benutzer und Zeitpunkt der Abholung werden dauerhaft im Verlauf protokolliert."}</p>
            </div>
            <div className="form-actions">
              <Button variant="secondary" onClick={() => setPendingAction(null)} disabled={Boolean(busyKey)}>Abbrechen</Button>
              {pendingAction.type === "register"
                ? <Button onClick={() => void registerReturn(pendingAction.warehouseId, pendingAction.warehouseLabel)} disabled={Boolean(busyKey)}><RotateCcw size={16} /> Verbindlich anmelden</Button>
                : <Button onClick={() => void completeReturn(pendingAction.orderReturn, pendingAction.warehouseLabel)} disabled={Boolean(busyKey)}><CheckCircle2 size={16} /> Abholung bestätigen</Button>}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
